import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, Paper, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useAuth } from '../../contexts/AuthContext';
import { useEnvironment } from '../../contexts/EnvironmentContext';
import { flowService } from '../../services/flowService';
import { describeLiveDataContract, buildOpenApiSpec } from '../../flow-core/apiSpec';

const typeOf = (schema) => {
  if (!schema) return 'string';
  if (schema.const !== undefined) return `always ${JSON.stringify(schema.const)}`;
  if (schema.type === 'array') return `list of ${typeOf(schema.items)}`;
  if (schema.format === 'date') return 'date (YYYY-MM-DD)';
  if (schema.enum) return `one of ${schema.enum.map((value) => JSON.stringify(value)).join(', ')}`;
  return schema.type || 'string';
};

const Code = ({ value, label }) => {
  const text = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
  return (
    <Box sx={{ position: 'relative' }}>
      <Tooltip title={`Copy ${label || ''}`.trim()}>
        <Button size="small" startIcon={<ContentCopyIcon fontSize="small" />}
          onClick={() => navigator.clipboard?.writeText(text)}
          sx={{ position: 'absolute', top: 4, right: 4 }}>
          Copy
        </Button>
      </Tooltip>
      <Box component="pre" sx={{
        m: 0, p: 2, pr: 10, bgcolor: 'grey.50', border: 1, borderColor: 'divider', borderRadius: 1,
        fontSize: 13, overflowX: 'auto', whiteSpace: 'pre',
      }}>
        {text}
      </Box>
    </Box>
  );
};

const FieldTable = ({ fields, withSource }) => (
  fields.length === 0 ? (
    <Typography variant="body2" color="text.secondary">No fields.</Typography>
  ) : (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Field</TableCell>
          <TableCell>Type</TableCell>
          {withSource && <TableCell>Comes from</TableCell>}
        </TableRow>
      </TableHead>
      <TableBody>
        {fields.map((field) => (
          <TableRow key={field.name}>
            <TableCell sx={{ fontFamily: 'monospace' }}>{field.name}</TableCell>
            <TableCell>{typeOf(field.schema)}</TableCell>
            {withSource && <TableCell>{field.source}</TableCell>}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
);

const VERIFY_NODE = `const crypto = require('crypto');

function isFromFlowGate(rawBody, signatureHeader, signingSecret) {
  const expected = 'sha256=' + crypto.createHmac('sha256', signingSecret)
    .update(rawBody).digest('hex');
  return crypto.timingSafeEqual(Buffer.from(expected), Buffer.from(signatureHeader || ''));
}`;

/**
 * One flow's live-data API, generated from the flow as it is right now: what FlowGate sends the
 * owner's server at each step that needs it, and what the server may reply. Nothing here is
 * stored; open it after changing the flow and it describes the changed flow.
 */
const FlowApiSpecPage = () => {
  const { flowId } = useParams();
  const navigate = useNavigate();
  const { authenticatedFetch } = useAuth();
  const { selectedEnvironment } = useEnvironment();
  const envId = selectedEnvironment?.id;
  const [flow, setFlow] = useState(null);
  const [live, setLive] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!envId || !flowId) return undefined;
    let cancelled = false;
    setError(null);
    Promise.all([
      flowService.getFlow(envId, flowId, authenticatedFetch),
      flowService.getLiveData ? flowService.getLiveData(envId, flowId, authenticatedFetch).catch(() => null) : null,
    ])
      .then(([loaded, liveData]) => { if (!cancelled) { setFlow(loaded); setLive(liveData); } })
      .catch((e) => { if (!cancelled) setError(e.message || 'Could not load this flow'); });
    return () => { cancelled = true; };
    // authenticatedFetch is recreated every AuthContext render; depending on it would refetch in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [envId, flowId]);

  const contract = useMemo(() => (flow ? describeLiveDataContract(flow.flowJson) : null), [flow]);
  const openApi = useMemo(
    () => (flow ? buildOpenApiSpec(flow.flowJson, { flowName: flow.name, flowId: flow.id }) : null), [flow]);

  if (error) return <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>;
  if (!flow || !contract) return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>;

  const server = live?.mode === 'FORWARD' && live.forwardUrl ? live.forwardUrl : 'your server';

  return (
    <Box sx={{ maxWidth: 980, mx: 'auto', px: 3, py: 3 }} data-testid="flow-api-spec">
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`/flow-builder/${flow.id}`)} sx={{ mb: 2 }}>
        Back to the flow
      </Button>
      <Typography variant="h5" component="h1" gutterBottom>{flow.name}: API</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        What FlowGate sends {server === 'your server' ? 'your server' : <strong>{server}</strong>} while a
        customer is in this flow, and what it can reply. Generated from the flow as it is now, so it
        changes when the flow does.
      </Typography>

      {!contract.dynamic ? (
        <Alert severity="info" data-testid="api-spec-static">
          This flow never calls a server: everything the customer sees is in the flow itself. To look
          something up while they fill it in, make a button send data (a data exchange) in the builder,
          then come back here.
        </Alert>
      ) : (
        <Stack spacing={3}>
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Every request</Typography>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              <code>POST</code> to {server === 'your server' ? 'your server' : <code>{server}</code>}, as JSON, with these
              headers. Reply within 8 seconds with HTTP 2xx and a JSON object. Anything else and WhatsApp
              shows the customer its own error, and the flow&apos;s Connections says why.
            </Typography>
            <Table size="small" sx={{ mb: 2 }}>
              <TableBody>
                <TableRow><TableCell sx={{ fontFamily: 'monospace' }}>X-FlowGate-Signature</TableCell><TableCell>sha256=&lt;HMAC-SHA256 of the raw body, keyed by this flow&apos;s signing secret&gt;</TableCell></TableRow>
                <TableRow><TableCell sx={{ fontFamily: 'monospace' }}>X-FlowGate-Timestamp</TableCell><TableCell>When FlowGate sent it, in Unix seconds</TableCell></TableRow>
                <TableRow><TableCell sx={{ fontFamily: 'monospace' }}>X-FlowGate-Flow</TableCell><TableCell sx={{ fontFamily: 'monospace' }}>{flow.id}</TableCell></TableRow>
              </TableBody>
            </Table>
            <Typography variant="body2" sx={{ mb: 1 }}>Check the signature before trusting a request:</Typography>
            <Code value={VERIFY_NODE} label="the check" />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              WhatsApp&apos;s health checks are answered by FlowGate and never reach you. The flow opens on
              its first screen, so your server is first called when the customer taps a button below.
            </Typography>
          </Paper>

          {contract.exchanges.map((exchange, index) => (
            <Paper variant="outlined" sx={{ p: 3 }} key={`${exchange.screenId}-${index}`} data-testid="api-spec-exchange">
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Chip size="small" label={`Step ${index + 1}`} />
                <Typography variant="h6">
                  When the customer taps &ldquo;{exchange.trigger}&rdquo; on {exchange.screenTitle}
                </Typography>
              </Stack>

              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>FlowGate sends</Typography>
              <FieldTable fields={exchange.fields} withSource />
              <Box sx={{ mt: 1.5 }}><Code value={exchange.request} label="the request" /></Box>

              <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>Your server replies with one of</Typography>
              <Stack spacing={2}>
                {exchange.replies.map((reply) => (
                  <Box key={`${reply.screenId}-${reply.sameScreen}`}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {reply.sameScreen
                        ? <>Stay on <strong>{reply.screenTitle}</strong> and show an error (set <code>error_message</code>)</>
                        : <>Show <strong>{reply.screenTitle}</strong></>}
                    </Typography>
                    <FieldTable fields={reply.fields} />
                    <Box sx={{ mt: 1 }}><Code value={reply.example} label="the reply" /></Box>
                  </Box>
                ))}
                <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>Or end the flow</Typography>
                  <Code value={{ screen: 'SUCCESS', data: { extension_message_response: { params: { flow_token: 'flow-token-from-whatsapp' } } } }} label="the reply" />
                </Box>
              </Stack>
            </Paper>
          ))}

          {contract.backScreens.length > 0 && (
            <Alert severity="info">
              Going back to {contract.backScreens.join(', ')} also calls your server, with{' '}
              <code>&quot;action&quot;: &quot;BACK&quot;</code>; reply with that screen and its data.
            </Alert>
          )}

          <Divider />
          <Box>
            <Typography variant="h6" gutterBottom>OpenAPI 3.1</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              The same contract for tools and code generators.
            </Typography>
            <Code value={openApi} label="the OpenAPI document" />
          </Box>
        </Stack>
      )}
    </Box>
  );
};

export default FlowApiSpecPage;
